import { useTranslation } from "i18n-fe";
import { useStationMonitor } from "../hooks/useStationMonitor";
import StationMonitorBanners from "./StationMonitorBanners";
import StationExceptionLine from "./StationExceptionLine";
import { summarizeStationMonitorRows } from "../util/stationMonitor";
import { elapsedMsSince, formatElapsedLabel } from "../util/scribePresence";

// #2827 (scribe-resilience workstream, G12 part B) -- the dashboard's glance at
// the Station Monitor. Deliberately reuses useStationMonitor, the SAME
// rows/summary the full StationMonitor page renders (util/stationMonitor.js's
// summarizeStationMonitorRows), so this strip and the monitor page can never
// disagree about a count. #3090: it renders inside the dashboard's Scribe
// section (ScribePresenceSection).
//
// #3646 (station-monitor workstream, Jaybill 2026-10-22): this area is for
// exceptions ONLY. There is no "all stations reporting clean" state and no
// "anyConnected" rule: when nothing is wrong it renders nothing at all. Every
// exception is ONE line (StationExceptionLine) that links to the Station
// Monitor, including each banner, which renders in its compact form here. The
// Station Monitor page itself keeps the full multi-line banners.
//
// The counts are one line, and there is none unless some worst-case number is
// non-zero or there is an oldest-unshipped timestamp (#2827 judge fix r1: a
// pile of merely-queued sheets is an exception; #3447: so is a tablet on an
// old build).
export default function StationMonitorStrip({ showId, pollMs }) {
  const { visibleRows, loading, error } = useStationMonitor(showId, pollMs === undefined ? undefined : { pollMs });
  return <StationMonitorStripView showId={showId} rows={visibleRows} loading={loading} error={error} />;
}

// #3647: the strip's body, fed rows by its caller. The Scribe Station Status
// section reads the station monitor itself (it also needs the rows to put each
// station's exception on its table row) and passes the rows it did NOT place on
// a row, so no exception is reported twice and none is dropped.
export function StationMonitorStripView({ showId, rows, loading = false, error = null }) {
  const { t } = useTranslation("scoreadmin");
  // #3454: the banners (stuck station, missing judge signature) are independent
  // of the counts line: a missing signature matters even with no scribe connected.
  const counts = !showId || loading || error ? null : renderCounts(summarizeStationMonitorRows(rows), t);
  return (
    <>
      <StationMonitorBanners showId={showId} rows={rows} compact />
      {counts}
    </>
  );
}

const SEPARATOR = " · ";

function renderCounts(summary, t) {
  const { needsAttentionTotal, queuedTotal, neverUploadedCount, oldestUnshippedAt, oldBuildCount } = summary;
  const parts = [];
  if (needsAttentionTotal > 0) {
    parts.push({ id: "needs-attention", text: t("stationMonitor.strip.needsAttention", { count: needsAttentionTotal }) });
  }
  if (queuedTotal > 0) {
    parts.push({ id: "queued", text: t("stationMonitor.strip.queued", { count: queuedTotal }) });
  }
  if (neverUploadedCount > 0) {
    parts.push({ id: "never-uploaded", text: t("stationMonitor.strip.neverUploaded", { count: neverUploadedCount }) });
  }
  if (oldBuildCount > 0) {
    parts.push({ id: "old-build", text: t("stationBuild.stripCount", { count: oldBuildCount }) });
  }
  if (oldestUnshippedAt) {
    parts.push({
      id: "oldest",
      text: t("stationMonitor.strip.oldestUnshipped", {
        time: formatElapsedLabel(elapsedMsSince(oldestUnshippedAt, Date.now())),
      }),
    });
  }
  // Nothing to report: render nothing, never a green all-clear.
  if (parts.length === 0) return null;

  return (
    <StationExceptionLine
      testId="station-monitor-strip"
      text={parts.map((p) => p.text).join(SEPARATOR)}
      linkTestId="station-monitor-strip-link"
    >
      {parts.map((p, i) => (
        <span key={p.id}>
          {i > 0 && SEPARATOR}
          <span data-testid={`station-monitor-strip-${p.id}`}>{p.text}</span>
        </span>
      ))}
    </StationExceptionLine>
  );
}
